import { useState, useRef } from "react";
import { phaseBarColor } from "./sheetHelpers";

export default function PhaseRow({ phase, taskCount, completedCount, collapsed, onToggle, onAddTask, onRename, onDelete, canManage }) {
  const pct = taskCount > 0 ? Math.round((completedCount / taskCount) * 100) : null;
  const barColor = phaseBarColor(pct);
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const renameRef = useRef(null);

  function startRename(e) {
    e.stopPropagation();
    setRenameDraft(phase.name);
    setRenaming(true);
    setTimeout(() => renameRef.current?.focus(), 30);
  }

  function commitRename() {
    setRenaming(false);
    const trimmed = renameDraft.trim();
    if (trimmed && trimmed !== phase.name) onRename(phase.id, trimmed);
  }

  return (
    <div
      data-phasename={phase.name}
      style={{ display: "flex", alignItems: "center", padding: "0 12px", height: 36, borderBottom: "1px solid var(--border)", borderLeft: "3px solid var(--phase)", background: "linear-gradient(to right, var(--phase-soft) 0%, var(--surface-2, #f8fafc) 100%)", cursor: renaming ? "default" : "pointer", userSelect: "none", gap: 8 }}
      onClick={renaming ? undefined : onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
    >
      <span style={{ fontSize: 10, color: "var(--phase)", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 12, flexShrink: 0 }}>▼</span>

      {renaming ? (
        <input
          ref={renameRef}
          value={renameDraft}
          onChange={e => setRenameDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitRename(); } if (e.key === "Escape") { setRenaming(false); } }}
          onClick={e => e.stopPropagation()}
          style={{ flex: 1, fontWeight: 700, fontSize: 12.5, color: "var(--phase-deep)", border: "1px solid var(--phase)", borderRadius: 4, padding: "1px 6px", outline: "none", background: "white", minWidth: 0 }}
        />
      ) : (
        <span
          style={{ fontWeight: 700, fontSize: 12.5, color: "var(--phase-deep)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          onDoubleClick={canManage ? startRename : undefined}
          title={canManage ? "Double-click to rename" : phase.name}
        >
          {phase.name}
        </span>
      )}

      {pct !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <div style={{ width: 48, height: 4, borderRadius: 3, background: "#e2e8f0", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 3, transition: "width 0.3s" }} />
          </div>
          <span style={{ fontSize: 11, color: "var(--phase)", fontWeight: 600, minWidth: 28, textAlign: "right" }}>{pct}%</span>
        </div>
      )}

      <span style={{ fontSize: 11, color: "var(--phase-muted)", whiteSpace: "nowrap" }}>{completedCount}/{taskCount} done</span>

      {canManage && !confirmDelete && (
        <button
          onClick={e => { e.stopPropagation(); onAddTask(); }}
          style={{ fontSize: 11, padding: "2px 9px", borderRadius: 5, border: "1px solid var(--phase-border)", background: "white", color: "var(--phase)", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", marginLeft: 2 }}
        >
          + Task
        </button>
      )}

      {canManage && !confirmDelete && hovered && (
        <button
          onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
          title="Delete phase"
          style={{ fontSize: 13, padding: "1px 5px", borderRadius: 4, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", opacity: 0.6, lineHeight: 1, flexShrink: 0 }}
        >
          ✕
        </button>
      )}
      {canManage && confirmDelete && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>Delete phase?</span>
          <button onClick={() => onDelete(phase.id)} style={{ fontSize: 11, padding: "1px 7px", borderRadius: 4, border: "none", background: "#dc2626", color: "white", cursor: "pointer", fontWeight: 600 }}>Yes</button>
          <button onClick={() => setConfirmDelete(false)} style={{ fontSize: 11, padding: "1px 7px", borderRadius: 4, border: "1px solid var(--border-strong)", background: "white", color: "var(--text-2)", cursor: "pointer" }}>No</button>
        </div>
      )}
    </div>
  );
}
